import React from 'react';
import { twMerge } from 'tailwind-merge';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type StpVersion = { lan?: 'none' | 'rstp'; lanprio?: number };
type StpPort = { port: number; state: string; role: string };

export default function WebUiSettingsStpPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [version, setVersion] = React.useState<StpVersion>({
    lan: 'none',
    lanprio: 8,
  });
  const [rootBridge, setRootBridge] = React.useState('');
  const [ports, setPorts] = React.useState<StpPort[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const loadStates = React.useCallback(async () => {
    const [root, portState] = await Promise.all([
      postWebUiApi<{ rootbridge?: string }>({
        service: 'stp',
        access: 'get',
        command: 'rootbridge',
      }),
      postWebUiApi<StpPort[]>({
        service: 'stp',
        access: 'get',
        command: 'port state',
      }),
    ]);
    setRootBridge(root.rootbridge ?? '');
    setPorts(portState ?? []);
  }, []);

  const reload = React.useCallback(async () => {
    const data = await postWebUiApi<StpVersion>({
      service: 'stp',
      access: 'get',
      command: 'get stp version',
    });
    setVersion(data);
    if (data.lan !== 'none') await loadStates();
  }, [loadStates]);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  return (
    <WebUiPageWrapper titleList={['Settings', 'Spanning Tree']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <h4 className="font-bold">Settings</h4>
          <table className="webui-table max-w-fit">
            <thead>
              <tr className="webui-table-row-head">
                <th>Option</th>
                <th>Setting</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Spanning Tree Protocol</td>
                <td>
                  <WebUiSelect
                    className="w-full"
                    disabled={readonly}
                    value={String(version.lan ?? 'none')}
                    options={[
                      { label: 'none', value: 'none' },
                      { label: 'RSTP', value: 'rstp' },
                    ]}
                    onChange={(e) =>
                      setVersion((p) => ({
                        ...p,
                        lan: e.target.value as 'none' | 'rstp',
                      }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Bridge Priority</td>
                <td>
                  <WebUiSelect
                    className="w-full"
                    disabled={readonly}
                    value={String(version.lanprio ?? 8)}
                    options={Array.from({ length: 16 }).map((_, idx) => ({
                      label: `${idx * 4096}`,
                      value: String(idx),
                    }))}
                    onChange={(e) =>
                      setVersion((p) => ({
                        ...p,
                        lanprio: Number(e.target.value),
                      }))
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div className="flex w-full gap-2">
            <WebUiButton
              disabled={readonly}
              onClick={async () => {
                try {
                  await postWebUiApi({
                    service: 'stp',
                    access: 'set',
                    command: 'set stp version',
                    parameters: {
                      lan: version.lan ?? 'none',
                      lanprio: Number(version.lanprio ?? 8),
                    },
                  });
                  setMessage('Spanning tree configuration updated.');
                  setError(null);
                  await reload();
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              Set Spanning Tree Configuration
            </WebUiButton>
          </div>
        </div>
        {version.lan !== 'none' && (
          <div className="mt-4 flex flex-row gap-4">
            <div className="flex flex-col gap-2">
              <h4 className="font-bold">Instance Information</h4>
              <table className="webui-table min-w-60 max-w-fit">
                <thead>
                  <tr className="webui-table-row-head">
                    <th>Parameter</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="webui-table-row-content">
                    <td>Root Bridge ID</td>
                    <td>{rootBridge || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-2">
              <h4 className="font-bold">Ports</h4>
              <table className="webui-table min-w-60 max-w-fit">
                <thead>
                  <tr className="webui-table-row-head">
                    <th>Port</th>
                    <th>State</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {ports.map((p) => (
                    <tr
                      key={p.port}
                      className={twMerge(
                        'webui-table-row-content',
                        p.state === 'forwarding'
                          ? 'bg-green-100'
                          : 'bg-red-100',
                      )}
                    >
                      <td>{p.port}</td>
                      <td>{p.state}</td>
                      <td>{p.role?.toLowerCase()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </WebUiPageWrapper>
  );
}
